import React, { useState, useContext, useEffect } from 'react';
import { FieldData, FieldOption, JobWorkflowDefaultItemsRowsFieldType } from 'types/FieldTypes';
import { useTranslation } from 'react-i18next';
import { useGlobalContext } from 'GlobalContext';
import { DragDropContext, Draggable } from 'react-beautiful-dnd';
import { StrictModeDroppable } from 'services/utils/dragDropUtils';
import { parseQuantityAndUnit } from 'services/utils/parseQuantityAndUnit';
import { v4 as uuidv4 } from 'uuid';
import FormFieldContext from '../FormFieldContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGripLines, faTrash, faCircleQuestion } from '@fortawesome/free-solid-svg-icons';
import { JobWorkflowDefaultItemType, createNewJobWorkflowDefaultItem } from '../../../views/settings/jobs/JobTypeDefaultsTypes';
import Dropdown from '../basefields/Dropdown';
import SearchSelect from '../basefields/SearchSelect';
import { renderRowsFieldErrorMessages } from './functions/renderRowsFieldErrorMessages';
import '../../../style/scss/live-edit.scss';
import '../../../style/scss/forms.scss';

// The two used status options of a default item
interface UsedStatusOption {
    id: number;
    name: string;
}

const JobWorkflowDefaultItemsRowsField: React.FC<JobWorkflowDefaultItemsRowsFieldType & { data: FieldData, viewKey: string }> = ({
    name, label, tooltipText, data
}) => {
    const { t } = useTranslation();
    const { errorMessages } = useGlobalContext();
    const { editing, setUpdatedData, showErrorAlert } = useContext(FormFieldContext);
    const [rows, setRows] = useState<JobWorkflowDefaultItemType[]>([]);

    // The two used status options of a default item, shown in the dropdown
    // Ids start at 1, the dropdown treats a falsy id as unset
    const usedStatusOptions: UsedStatusOption[] = [
        { id: 1, name: t('job.job_type.default_items.used_status_option_done') },
        { id: 2, name: t('job.job_type.default_items.used_status_option_to_bring') },
    ];

    // If there are default items in the fetched data, show them. Otherwise show an empty row
    useEffect(() => {
        if (data && data[name] && data[name].length > 0) {
            const sortedRows = (data[name] || []).sort(
                (a: JobWorkflowDefaultItemType, b: JobWorkflowDefaultItemType) => (a.ordering ?? 0) - (b.ordering ?? 0)
            ).map((row: JobWorkflowDefaultItemType) => ({ ...row }));
            setRows(sortedRows);
        } else {
            const firstPosition = 1;
            setRows([createNewJobWorkflowDefaultItem(uuidv4(), firstPosition)]);
        }
    }, [data]);

    // Update the rows state and updated data
    const syncUpdatedData = (updatedRows: JobWorkflowDefaultItemType[]) => {
        setRows(updatedRows);
        setUpdatedData((current: any) => ({ ...current, [name]: updatedRows }));
    };

    // Update a row
    const updateRow = (identifier: string, updater: (row: JobWorkflowDefaultItemType) => JobWorkflowDefaultItemType) => {
        const updatedRows = rows.map(row =>
            row.id?.toString() === identifier || row.uuid === identifier ? updater(row) : row
        );
        syncUpdatedData(updatedRows);
    };

    // Map a default item to the field option of the product search field
    const itemNameToFieldOption = (row: JobWorkflowDefaultItemType): FieldOption | null => {
        if (row.product !== null) {
            return { id: row.product, value: row.name, name: row.name };
        } else if (row.name) {
            return { value: row.name, name: row.name };
        }
        return null;
    };

    // Handle the quantity change
    const handleQuantityChange = (event: React.ChangeEvent<HTMLInputElement>, identifier: string) => {
        const { quantity } = parseQuantityAndUnit(event.target.value);
        updateRow(identifier, row => ({
            ...row,
            quantity_string: event.target.value,
            quantity: quantity,
        }));
    };

    // Handle the selection of an existing product
    const handleNameChange = (selectedItem: any, identifier: string) => {
        updateRow(identifier, row => {
            if (selectedItem && selectedItem.id) {
                return {
                    ...row,
                    product: selectedItem.id,
                    name: selectedItem.name,
                    number: selectedItem.number !== null ? selectedItem.number : null,
                };
            }
            return row;
        });
    };

    // Handle the unlink of the selected product
    const handleNameUnlink = (identifier: string) => {
        updateRow(identifier, row => ({ ...row, product: null, name: '', number: null }));
    };

    // Handle the used status change
    const handleUsedStatusChange = (identifier: string, value: string) => {
        updateRow(identifier, row => ({ ...row, initial_used: value === '1' }));
    };

    // Handle the creation of a new row
    const handleAddRow = () => {
        // Split rows in non deleted and deleted rows
        const nonDeletedRows = rows.filter(row => !row.deleted);
        const deletedRows = rows.filter(row => row.deleted);

        // Determine the ordering of the new row and create it
        const newOrdering = nonDeletedRows.length + 1;
        const newRow = createNewJobWorkflowDefaultItem(uuidv4(), newOrdering);

        // Add the new row at the end of the list with non deleted rows and add the deleted rows
        const updatedRows = [...nonDeletedRows, newRow, ...deletedRows];

        syncUpdatedData(updatedRows);
    };

    // Handle deletion of a row
    const handleDeleteRow = (identifier: string) => {
        const updatedRows = [...rows];
        const rowIndex = updatedRows.findIndex(row => row.id?.toString() === identifier || row.uuid === identifier);

        if (rowIndex !== -1) {
            // Mark the row as deleted without actually deleting it
            updatedRows[rowIndex].deleted = true;

            // Update the ordering of the rows which are not marked as deleted
            const nonDeletedRows = updatedRows.filter(row => !row.deleted);
            const reorderedNonDeletedRows = nonDeletedRows.map((row, index) => ({
                ...row,
                ordering: index + 1,
            }));

            // Add the deleted rows to the reordered list to keep them for the updated data
            const finalRows = [...reorderedNonDeletedRows, ...updatedRows.filter(row => row.deleted)];

            syncUpdatedData(finalRows);
        }
    };

    // Handle ordering change
    const handleOrderingChange = (result: any) => {
        if (!result.destination) return;
        const { source, destination } = result;

        // Only use the non deleted rows to determine the new ordering
        const nonDeletedRows = rows.filter(row => !row.deleted);
        const [reorderedRow] = nonDeletedRows.splice(source.index, 1);
        nonDeletedRows.splice(destination.index, 0, reorderedRow);

        // Determine the ordering for the non deleted rows
        const updatedNonDeletedRows = nonDeletedRows.map((row, index) => ({
            ...row,
            ordering: index + 1,
        }));

        // Add the deleted rows to the list without changing their ordering
        const finalRows = [...updatedNonDeletedRows, ...rows.filter(row => row.deleted)];

        syncUpdatedData(finalRows);
    };

    return (
        <div className='rows-field job-type-defaults-field'>
            {label &&
                <label>
                    {t(label)}
                    {tooltipText &&
                        <span className="tooltip-icon">
                            <FontAwesomeIcon icon={faCircleQuestion} />
                            <span className="tooltip tooltip-right">{t(tooltipText)}</span>
                        </span>
                    }
                </label>
            }
            {showErrorAlert &&
                <div className="alert form-alert alert-danger" role="alert">
                    {t(errorMessages.general, { defaultValue: errorMessages.general })}
                </div>
            }
            {editing && (
                <div className='edit-mode'>
                    {rows.length > 0 && (
                        <div className='rows-header default-item-row'>
                            <div className='header-item'>{t('job.items.quantity_label')}</div>
                            <div className='header-item'>{t('job.items.description_label')}</div>
                            <div className='header-item'>
                                {t('job.job_type.default_items.used_status_label')}
                                <span className="tooltip-icon">
                                    <FontAwesomeIcon icon={faCircleQuestion} />
                                    <span className="tooltip tooltip-right">
                                        {t('job.job_type.default_items.used_status_tooltip')}
                                    </span>
                                </span>
                            </div>
                            <div className='delete-placeholder'></div>
                            <div className='drag-placeholder'></div>
                        </div>
                    )}
                    <DragDropContext onDragEnd={handleOrderingChange}>
                        <StrictModeDroppable droppableId="droppable">
                            {(provided) => (
                                <div {...provided.droppableProps} ref={provided.innerRef}>
                                    {rows && rows.filter(row => row.deleted === false).map((row, index) => {
                                        const identifier = row.id?.toString() || row.uuid || '';
                                        return (
                                            <Draggable key={identifier} draggableId={`default-item-${identifier}`} index={index} isDragDisabled={rows.length <= 1}>
                                                {(provided) => (
                                                    <div className='list-item draggable' ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                                                        <div className='default-item-row'>
                                                            <input
                                                                type="text"
                                                                id={`quantity_string_${identifier}`}
                                                                name={`quantity_string_${identifier}`}
                                                                value={row.quantity_string ?? ''}
                                                                onChange={event => handleQuantityChange(event, identifier)}
                                                                placeholder={t('job.items.quantity_placeholder')}
                                                                className={errorMessages?.default_items?.[identifier]?.['quantity'] ? 'is-invalid' : undefined}
                                                            />
                                                            <SearchSelect
                                                                key={identifier}
                                                                type='searchselect'
                                                                name='name'
                                                                value={itemNameToFieldOption(row)}
                                                                onSelect={(selectedItem) => handleNameChange(selectedItem, identifier)}
                                                                onUnlink={() => handleNameUnlink(identifier)}
                                                                placeholder='invoice.lines.search_item_label'
                                                                objectName='job.items.article_description_object_name'
                                                                query={{ endpoint: 'get_product_list', params: { is_active: true } }}
                                                                disableAddNew={true}
                                                                optionFormat={{
                                                                    title: { field: 'name', format: (value) => value },
                                                                    bracket: { field: 'number', format: (value) => value, optional: true },
                                                                }}
                                                                selectionFormat='name'
                                                                isInvalid={!!errorMessages?.default_items?.[identifier]?.['name']}
                                                            />
                                                            <Dropdown<UsedStatusOption>
                                                                name={`used_status_${identifier}`}
                                                                options={usedStatusOptions}
                                                                selectedOption={usedStatusOptions.find(option => option.id === (row.initial_used ? 1 : 2)) ?? usedStatusOptions[1]}
                                                                backendField='id'
                                                                optionFormat='name'
                                                                selectionFormat={(option) => option.name}
                                                                showSearch={false}
                                                                allowNoneOption={false}
                                                                onChange={(value) => handleUsedStatusChange(identifier, value)}
                                                            />
                                                            <div className='delete-icon tooltip-icon'>
                                                                <FontAwesomeIcon icon={faTrash} onClick={() => handleDeleteRow(identifier)} />
                                                                <span className="tooltip">{t('general.delete')}</span>
                                                            </div>
                                                            <div className={`order-icon tooltip-icon ${rows.length === 1 ? 'visibility-hidden' : ''}`}>
                                                                <FontAwesomeIcon icon={faGripLines} />
                                                                <span className="tooltip">{t('general.reorder')}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </Draggable>
                                        );
                                    })}
                                    {provided.placeholder}
                                </div>
                            )}
                        </StrictModeDroppable>
                    </DragDropContext>
                    {errorMessages &&
                        <div className='error-message'>
                            {renderRowsFieldErrorMessages(errorMessages, 'default_items', t)}
                        </div>
                    }
                    <div className='add-new-row-actions'>
                        <div onClick={(e) => { e.preventDefault(); handleAddRow(); }}
                            className="add-new-button">
                            {t('job.job_type.default_items.add_new_article')}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default JobWorkflowDefaultItemsRowsField;
